// frontend/shared/src/api/client.ts —— spec §2 api / D8-11 / TD-811 / TD-812
import type { TokenStore } from '../storage';
import { ROUTES, routeAud, splitRoute, type Aud, type RouteKey } from './routes';
import type { ErrorData } from './types';

export const NETWORK_CODE = -1;
// idle_timeout / max_age：服务端以 401 + data.reason（session_idle_timeout / session_max_age）宣告会话已吊销（2026-10-15）
export type SessionLostReason = 'expired' | 'miniapp_expired' | 'idle_timeout' | 'max_age';
export interface SessionLostDetail {
  maxHours?: number; // reason=max_age 时取 data.max_hours（正整数）；缺失则调用方退化为通用过期文案
}
// 带原因的会话终止（服务端已吊销，不得再 refresh：refresh 只会得到无 reason 的普通 401，原因即丢失）
export interface SessionLoss {
  reason: 'idle_timeout' | 'max_age';
  detail: SessionLostDetail;
}
export type QueryValue = string | number | boolean | null | undefined;

export interface CallOptions {
  path?: Readonly<Record<string, string | number>>;
  query?: Readonly<Record<string, QueryValue>>;
  body?: unknown;
  // multipart 表单体（02 §4.4 KYC 提交）：存在时请求体即该 FormData，不设 Content-Type（由运行时带 boundary）；与 body 互斥
  form?: FormData;
  signal?: AbortSignal;
  // 定时轮询等非用户触发的请求（2026-10-15）：带 `X-Activity: passive`，服务端照常判空闲 / 最长在线但不刷新最后活跃；
  // 也不计入前端保活基准（onAuthedRequest），否则前端会误以为刚与服务端交互过而跳过保活
  passive?: boolean;
}

export const ACTIVITY_HEADER = 'X-Activity';
export const ACTIVITY_PASSIVE = 'passive';

export interface ClientOptions {
  base: string;
  aud: Aud;
  tokens: TokenStore;
  fetchImpl?: typeof fetch;
  isMiniApp?: () => boolean;
  onSessionLost?: (reason: SessionLostReason, detail?: SessionLostDetail) => void;
  // 每次向服务端发出带 access 的请求或 refresh 时回调（空闲保活节流基准，见 shared/src/idle.ts）
  onAuthedRequest?: () => void;
  log?: (event: string, detail: Readonly<Record<string, unknown>>) => void;
}

export class ApiError extends Error {
  readonly code: number;
  readonly status: number;
  readonly data: ErrorData;
  readonly retryAfter: number | null;
  readonly route: string;
  constructor(p: { code: number; status: number; message: string; data: ErrorData; route: string }) {
    super(p.message);
    this.name = 'ApiError';
    this.code = p.code;
    this.status = p.status;
    this.data = p.data;
    this.route = p.route;
    this.retryAfter = typeof p.data.retry_after === 'number' ? p.data.retry_after : null;
  }
  get isNetwork(): boolean {
    return this.code === NETWORK_CODE;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function toErrorData(v: unknown): ErrorData {
  if (!isRecord(v)) return {};
  const d: ErrorData = {};
  if (typeof v.need_2fa === 'boolean') d.need_2fa = v.need_2fa;
  if (typeof v.retry_after === 'number') d.retry_after = v.retry_after;
  if (typeof v.field === 'string') d.field = v.field;
  if (v.over_cap !== undefined) d.over_cap = v.over_cap;
  if (typeof v.tx_hash === 'string') d.tx_hash = v.tx_hash;
  if (typeof v.reason === 'string') d.reason = v.reason;
  if (typeof v.inflight_biz_no === 'string') d.inflight_biz_no = v.inflight_biz_no;
  if (typeof v.inflight_action === 'string') d.inflight_action = v.inflight_action;
  if (typeof v.awaiting_manual === 'boolean') d.awaiting_manual = v.awaiting_manual;
  if (typeof v.error_code === 'string') d.error_code = v.error_code;
  if (typeof v.pending_count === 'number') d.pending_count = v.pending_count;
  if (Array.isArray(v.steps)) d.steps = v.steps;
  if (Array.isArray(v.items)) d.items = v.items;
  if (isRecord(v.refreshed_quote)) d.refreshed_quote = v.refreshed_quote;
  if (typeof v.max_hours === 'number' && Number.isFinite(v.max_hours)) d.max_hours = v.max_hours;
  if (typeof v.open_fee === 'string') d.open_fee = v.open_fee;
  if (typeof v.missing === 'string') d.missing = v.missing;
  if (typeof v.max === 'string') d.max = v.max;
  return d;
}

// 401 信封 data → 带原因的会话终止；非会话类原因返回 null
export function sessionLossOf(status: number, data: ErrorData): SessionLoss | null {
  if (status !== 401) return null;
  if (data.reason === 'session_idle_timeout') return { reason: 'idle_timeout', detail: {} };
  if (data.reason === 'session_max_age') {
    const h = data.max_hours;
    return { reason: 'max_age', detail: typeof h === 'number' && Number.isInteger(h) && h > 0 ? { maxHours: h } : {} };
  }
  return null;
}

// 不消费原响应体：克隆后读取，调用方仍可按原样读取信封抛 ApiError
async function peekSessionLoss(res: Response): Promise<SessionLoss | null> {
  try {
    const payload: unknown = await res.clone().json();
    if (!isRecord(payload)) return null;
    return sessionLossOf(res.status, toErrorData(payload.data));
  } catch {
    return null;
  }
}

export function buildPath(template: string, params: Readonly<Record<string, string | number>> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_m, name: string) => {
    const v = params[name];
    if (v === undefined || v === '') throw new Error(`路径参数缺失：${name}（${template}）`);
    return encodeURIComponent(String(v));
  });
}

export function buildQuery(q: Readonly<Record<string, QueryValue>> = {}): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export class ApiClient {
  private refreshing: Promise<boolean> | null = null;
  private readonly fetchImpl: typeof fetch;
  private loss: SessionLoss | null = null;

  constructor(private readonly opts: ClientOptions) {
    this.fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  get aud(): Aud {
    return this.opts.aud;
  }

  get tokens(): TokenStore {
    return this.opts.tokens;
  }

  // 最近一次 refresh 失败所带的会话终止原因（每次 refresh 开始时清空）。
  // 启动期 tryRefresh 不回调 onSessionLost，调用方据此在登录页提示「空闲退出 / 超过最长在线」
  get lastLoss(): SessionLoss | null {
    return this.loss;
  }

  // 响应体在信封边界处收窄为调用方声明的类型（唯一一处信任后端契约的位置，字段白名单见 02）
  async call<T = unknown>(route: RouteKey, o: CallOptions = {}): Promise<T> {
    const res = await this.send(route, o, false);
    const env = await this.readEnvelope(res, route);
    return env as T;
  }

  async blob(route: RouteKey, o: CallOptions = {}): Promise<{ blob: Blob; filename: string | null }> {
    const res = await this.send(route, o, false);
    const ct = res.headers.get('content-type') ?? '';
    if (!res.ok || ct.includes('application/json')) {
      await this.readEnvelope(res, route);
      throw new ApiError({ code: 5000, status: res.status, message: 'unexpected json', data: {}, route });
    }
    const cd = res.headers.get('content-disposition') ?? '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return { blob: await res.blob(), filename: m?.[1] ? decodeURIComponent(m[1]) : null };
  }

  private async send(route: RouteKey, o: CallOptions, replayed: boolean): Promise<Response> {
    const def = ROUTES[route];
    if (def.auth === 'bearer' && routeAud(route) !== this.opts.aud) throw new Error(`受众不匹配：${route} 不属于 ${this.opts.aud} 客户端`);
    const { method, path } = splitRoute(route);
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (this.opts.isMiniApp?.()) headers['X-Client'] = 'miniapp';
    const token = this.opts.tokens.get();
    if (o.passive) headers[ACTIVITY_HEADER] = ACTIVITY_PASSIVE;
    if (def.auth === 'bearer' && token) {
      headers.Authorization = `Bearer ${token}`;
      if (!o.passive) this.opts.onAuthedRequest?.();
    }
    if (o.form !== undefined && o.body !== undefined) throw new Error(`请求体冲突：${route} 不得同时给 body 与 form`);
    let body: string | FormData | undefined;
    if (method !== 'GET' && method !== 'DELETE') {
      if (o.form !== undefined) {
        body = o.form;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(o.body ?? {});
      }
    }
    const url = this.opts.base + buildPath(path, o.path) + buildQuery(o.query);
    let res: Response;
    try {
      res = await this.fetchImpl(url, { method, headers, body, credentials: 'same-origin', signal: o.signal });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      this.opts.log?.('network_error', { route, message: e instanceof Error ? e.message : String(e) });
      throw new ApiError({ code: NETWORK_CODE, status: 0, message: 'network', data: {}, route });
    }
    if (res.status === 401 && def.auth === 'bearer' && !replayed) {
      // 401 只对发请求时所持 token 成立：此间 token 已换新（并发 refresh / 重新登录）→ 仅 GET 用当前 token 重放一次；
      // 非 GET 不重放（换人后重放会把上一会话发起的写操作记到新会话名下，审计归属错位），原样返回 401；
      // 已被清空（退出 / 会话结束）→ 原样返回。两种情形均不 refresh、不回调 onSessionLost（旧请求的 401 不得结束新会话）
      const cur = this.opts.tokens.get();
      if (cur !== token) {
        if (cur && method === 'GET') return this.send(route, o, true);
        return res;
      }
      // 服务端已按空闲 / 最长在线吊销本会话：不 refresh（会丢失原因），直接结束会话并原样返回（调用方读信封得 ApiError 1001 + data.reason）
      const loss = this.opts.isMiniApp?.() ? null : await peekSessionLoss(res);
      if (loss) {
        this.loss = loss;
        this.opts.tokens.clear();
        this.opts.onSessionLost?.(loss.reason, loss.detail);
        return res;
      }
      const renewed = await this.renew(o.passive === true);
      if (renewed) return this.send(route, o, true);
    }
    return res;
  }

  // 启动期无 token 时试探 refresh cookie（06 U-20 启动顺序④）；不触发 onSessionLost（带原因的失败经 lastLoss 读取）
  async tryRefresh(): Promise<boolean> {
    if (this.opts.isMiniApp?.()) return false;
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  // 主动续期（空闲提示框「继续使用」）：等价于一次 401 后的 refresh，失败按原因回调 onSessionLost
  async renewSession(): Promise<boolean> {
    return this.renew();
  }

  // passive：由被动请求（定时轮询）的 401 触发 → refresh 同样带 X-Activity: passive（后端照常判超时与轮换，
  // 但后继会话继承前身最后活跃，不计为活跃）。并发共用同一个在途 refresh 时以**先发起者**为准：
  // 被动先发起则这次 refresh 为被动；随后加入的主动请求在 refresh 成功后会以新 token 重放，重放本身是主动请求，
  // 服务端最后活跃随即刷新，不会因此误判空闲。「继续使用」与启动期 tryRefresh 恒为主动（不带头）
  private async renew(passive = false): Promise<boolean> {
    const { tokens, isMiniApp, onSessionLost } = this.opts;
    if (isMiniApp?.()) {
      tokens.clear();
      onSessionLost?.('miniapp_expired');
      return false;
    }
    if (!tokens.isPersistent()) {
      tokens.clear();
      onSessionLost?.('expired');
      return false;
    }
    if (!this.refreshing) {
      this.refreshing = this.doRefresh(passive).finally(() => {
        this.refreshing = null;
      });
    }
    const ok = await this.refreshing;
    if (!ok) {
      tokens.clear();
      const loss = this.loss;
      if (loss) onSessionLost?.(loss.reason, loss.detail);
      else onSessionLost?.('expired');
    }
    return ok;
  }

  private async doRefresh(passive = false): Promise<boolean> {
    const route: RouteKey = `POST /api/${this.opts.aud}/refresh`;
    this.loss = null;
    // 被动 refresh 不计入前端保活基准（同被动请求）
    if (!passive) this.opts.onAuthedRequest?.();
    const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json' };
    if (passive) headers[ACTIVITY_HEADER] = ACTIVITY_PASSIVE;
    try {
      const res = await this.fetchImpl(this.opts.base + splitRoute(route).path, {
        method: 'POST',
        headers,
        body: '{}',
        credentials: 'same-origin',
      });
      const data = await this.readEnvelope(res, route);
      if (isRecord(data) && typeof data.token === 'string' && data.token) {
        this.opts.tokens.set(data.token, true);
        return true;
      }
      return false;
    } catch (e) {
      if (e instanceof ApiError) this.loss = sessionLossOf(e.status, e.data);
      this.opts.log?.('refresh_failed', { message: e instanceof Error ? e.message : String(e), ...(this.loss ? { reason: this.loss.reason } : {}) });
      return false;
    }
  }

  private async readEnvelope(res: Response, route: string): Promise<unknown> {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }
    if (isRecord(payload) && typeof payload.code === 'number') {
      if (res.ok && payload.code === 0) return payload.data;
      throw new ApiError({ code: payload.code, status: res.status, message: typeof payload.message === 'string' ? payload.message : '', data: toErrorData(payload.data), route });
    }
    throw new ApiError({ code: 5000, status: res.status, message: res.statusText || 'bad response', data: {}, route });
  }
}
